import { useQuery } from '@tanstack/react-query';

import { UsageError } from '@/features/workspace-usage';

import { listConnections } from '@/lib/api/generated/api';
import type { ConnectionView } from '@/lib/api/generated/model';

import { connectionKeys } from '../queryKeys';

/**
 * Every connection configured on a workspace, enabled and disabled alike
 * (FR-001).
 *
 * Same hand-written-`queryFn` convention as `useTriggerProfiles`/
 * `useWorkspaceSecrets`: calls the generated raw `listConnections` fetcher
 * directly, not the generated `useListConnections` hook — no feature in this
 * codebase actually consumes orval's generated query hooks.
 *
 * Anything but a 200 throws a typed `UsageError` rather than resolving to an empty list — a
 * workspace with connections already configured must not look like one with
 * none after a failed load.
 */
export function useConnections(
  workspaceId: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const query = useQuery({
    queryKey: connectionKeys.list(workspaceId),
    queryFn: async (): Promise<ConnectionView[]> => {
      const response = await listConnections(workspaceId);
      // Typed, so a page switches on `kind` rather than on message text — the rule
      // `UsageError` records. Deliberately only `unavailable`: this endpoint's contract
      // is 200/401/422, so it has no 403 to map, and a `forbidden` branch here would be
      // unreachable code claiming a distinction the API cannot make. A refusal on this
      // surface is a 401, which the auth layer handles before a page ever renders.
      if (response.status !== 200) {
        throw new UsageError(
          'unavailable',
          'The connections could not be loaded.',
        );
      }
      return response.data;
    },
    // Off for a reader the strip must not count for (spec 229): the request is
    // never made, and `connections` stays undefined.
    enabled,
    retry: false,
  });

  return {
    connections: query.data,
    isPending: query.isPending,
    loadFailed: query.isError,
    refetch: query.refetch,
  };
}
